import { getApi } from "../Services/apiService";
import {
  formatDashboardFullPersonName,
  getDashboardMemberNameParts,
} from "./dashboardMemberDisplayName";
import { calculateAge, getMemberDobTimestamp } from "./family-tree/dateAndTime";
import { normalizeStateName } from "../constants/states";

/**
 * The "Descendants by State" report — GET /report-management/family-by-state
 * (rpt_FamByStates.pdf, "Family and/or Descendants by State").
 *
 * The endpoint lists living members only (present_or_deceased 2 is left
 * out), each row with its family-line colour and the head of their family
 * line — the generation-2 ancestor. It groups them itself, but by state of
 * birth on some backends, so the page re-files every row under the state
 * the member lives in now (current_state_residing), falling back to the
 * endpoint's group only for a row whose record leaves that blank. The
 * detail page
 * reads its list and its heading's totals through here; the dashboard card
 * reads the same two totals off the dashboard statistics block, which
 * states them as family_by_state_count and total_living_members_by_state.
 *
 * Sample payload:
 *   { family_tree_id, total_states: 5,
 *     states: [ { state: "Georgia", count: 10, members: [ {
 *       id, name: "John Doe", first_name, middle_name, last_name, suffix,
 *       special_name, color_name: "Gold", hex_code: "#FFD700",
 *       generation_level: 3, state_of_birth: "Georgia",
 *       current_state_residing: "Alabama",
 *       head_first_name: "Mike", head_last_name: "Smith",
 *       head_suffix: "Sr.", head_special_name: null } ] } ] }
 */

/** Printed for a row whose state the record leaves blank. */
export const UNKNOWN_STATE_LABEL = "Unknown";

/**
 * A member this old or younger lives with their parents, so the report files
 * them under their parent's state rather than their own state of birth.
 */
export const DESC_BY_STATE_MINOR_MAX_AGE = 17;

/** Whole number or null — the report prints "—" for anything it cannot read. */
const toNumberOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const toText = (value) => String(value ?? "").trim();

/**
 * The name in the Name column, spelled the way every other dashboard list
 * spells a member: first name, then the suffix after a comma or the special
 * initial after a space — "Ted, Sr.", "Josephine M", "Frank, Jr.".
 *
 * Built from the row's name parts rather than the endpoint's own `name`,
 * which is the full "John Doe". That string is the fallback for a row that
 * arrives with no first name.
 */
function buildMemberName(member) {
  const { baseName, descendantSpouseName } = getDashboardMemberNameParts(
    member,
    { unnamedLabel: "", strictHeadForInLaw: true }
  );

  if (baseName) return { baseName, descendantSpouseName };

  return { baseName: toText(member?.name), descendantSpouseName: "" };
}

/**
 * The head of the row's family line, as the endpoint names them: first name
 * with their suffix or special initial — "Mike, Sr.", "Mary M" — the same
 * short form the other reports print before a line's colour.
 */
export function formatDescByStateHeadName(member) {
  const firstName = toText(member?.head_first_name);
  if (!firstName) return "";

  const suffix = toText(member?.head_suffix);
  if (suffix) return `${firstName}, ${suffix}`;

  const special = toText(member?.head_special_name);
  return special ? `${firstName} ${special}` : firstName;
}

/**
 * The state a member lives in now, spelled the way the State dropdown files
 * it ("GA" → "Georgia"); the raw value when it is not one of the 50, and ""
 * when the record leaves it blank.
 */
function readCurrentState(member) {
  const raw = toText(member?.current_state_residing);
  return normalizeStateName(raw) || raw;
}

/** One API row → the columns the report prints, under the state it is filed in. */
function toReportRow(member, index, state) {
  const { baseName, descendantSpouseName } = buildMemberName(member);

  return {
    id: String(member?.id ?? `desc-by-state-${index}`),
    member,
    state,
    // Whether the record names where the member lives now; only a minor
    // without one is filed with their parent — see fileMinorsWithParents.
    hasOwnState: Boolean(readCurrentState(member)),
    baseName,
    descendantSpouseName,
    // What the A-Z sort reads, so it sorts on the name as printed.
    sortName: `${baseName} ${descendantSpouseName}`.trim(),
    headName: formatDescByStateHeadName(member),
    colorName: toText(member?.color_name),
    hexCode: toText(member?.hex_code) || "#000000",
  };
}

/**
 * The states with their rows, each row under the state its member lives in
 * now, in the order those states first appear, each with its own count. The
 * page's totals come off these once the minors have been re-filed — see
 * getDescByStateTotals.
 */
export async function fetchFamilyByState(familyTreeId) {
  if (!familyTreeId) return [];

  const response = await getApi(
    `/report-management/family-by-state?family_tree_id=${familyTreeId}`
  );

  if (response?.statusCode !== 200 || !response?.data) return [];

  const groupsByKey = new Map();
  (Array.isArray(response.data.states) ? response.data.states : []).forEach(
    (group, groupIndex) => {
      const endpointState = toText(group?.state);
      const members = Array.isArray(group?.members) ? group.members : [];
      members.forEach((member, index) => {
        const state =
          readCurrentState(member) ||
          normalizeStateName(endpointState) ||
          endpointState ||
          UNKNOWN_STATE_LABEL;
        const key = state.toLowerCase();
        if (!groupsByKey.has(key)) groupsByKey.set(key, { key, state, rows: [] });
        groupsByKey
          .get(key)
          .rows.push(toReportRow(member, `${groupIndex}-${index}`, state));
      });
    }
  );

  return [...groupsByKey.values()].map((group) => ({
    ...group,
    count: group.rows.length,
  }));
}

/**
 * The family tree's root nodes with every generation nested under them in
 * `children` — the same tree Desc Parent and Child(ren) reads — for the
 * parent each member hangs under and their date of birth. Empty when the
 * tree cannot be read, in which case no one moves.
 */
export async function fetchDescByStateFamilyTree(familyTreeId) {
  if (!familyTreeId) return [];

  const response = await getApi(
    `/family-tree-hierarchy-v2/${encodeURIComponent(familyTreeId)}`,
    { params: { values: "old" } }
  );
  if (response?.statusCode !== 200 || !response?.data) return [];

  if (Array.isArray(response.data.tree)) return response.data.tree;
  return Array.isArray(response.data) ? response.data : [];
}

const nodeId = (node) => toText(node?.id ?? node?._id ?? node?.member_id);

const stateOf = (states, key) => states.find((group) => group.key === key)?.state;

/** Full years old today off the record's date of birth; null without one. */
function readAgeToday(record) {
  const dobMs = getMemberDobTimestamp(record);
  if (dobMs === -1) return null;
  return toNumberOrNull(calculateAge(new Date(dobMs).toISOString().slice(0, 10)));
}

/**
 * The family tree flattened by member id — each node with the id of the
 * node it hangs under, which is the parent the tree names for them. Read
 * off /family-tree-hierarchy-v2, whose root members nest every generation
 * under them in `children`.
 */
export function indexFamilyTreeByMember(roots) {
  const byId = new Map();
  const queue = (Array.isArray(roots) ? roots : []).map((node) => ({
    node,
    parentId: "",
  }));

  while (queue.length > 0) {
    const { node, parentId } = queue.shift();
    const id = nodeId(node);
    if (id && !byId.has(id)) byId.set(id, { node, parentId });
    (Array.isArray(node?.children) ? node.children : []).forEach((child) =>
      queue.push({ node: child, parentId: id })
    );
  }

  return byId;
}

/**
 * The states with every minor — DESC_BY_STATE_MINOR_MAX_AGE or younger today,
 * by the date of birth on their tree record — whose record does not say
 * where they live moved under the state their parent is filed in, since a
 * child lives with their parents. A minor whose record names a current
 * state stays under it. The parent is the member the child hangs under in the family
 * tree; where that parent is themselves a minor the child follows them to
 * wherever they were filed. A minor whose parent is not on the report (the
 * endpoint leaves out the deceased) or whose age or parent the tree does not
 * know stays where the endpoint put them.
 *
 * Every state keeps its place; its count becomes how many rows are under it
 * once the minors have moved, and a state left with no one drops out. Each
 * row's `state` is re-pointed at the state it now sits in.
 *
 * @param {Array} states — the groups fetchFamilyByState returns
 * @param {Map} treeByMember — indexFamilyTreeByMember over the family tree
 */
export function fileMinorsWithParents(states, treeByMember) {
  if (!treeByMember || treeByMember.size === 0) return states;

  const groupKeyByRowId = new Map();
  const rowsById = new Map();
  states.forEach((group) =>
    group.rows.forEach((row) => {
      groupKeyByRowId.set(row.id, group.key);
      rowsById.set(row.id, row);
    })
  );

  // The state each row lands in, resolved parent-first so a minor under a
  // minor follows them; a row already being resolved (a cycle in the tree
  // data) is left where it is rather than chased around it.
  const resolved = new Map();
  const resolving = new Set();
  const landingKey = (rowId) => {
    if (resolved.has(rowId)) return resolved.get(rowId);
    const own = groupKeyByRowId.get(rowId);
    if (resolving.has(rowId)) return own;
    resolving.add(rowId);

    let key = own;
    const entry = rowsById.get(rowId)?.hasOwnState
      ? null
      : treeByMember.get(rowId);
    const age = entry ? readAgeToday(entry.node) : null;
    const isMinor = age !== null && age <= DESC_BY_STATE_MINOR_MAX_AGE;
    if (isMinor && entry.parentId && groupKeyByRowId.has(entry.parentId)) {
      key = landingKey(entry.parentId);
    }

    resolving.delete(rowId);
    resolved.set(rowId, key);
    return key;
  };

  // Who stays and who arrives, by state: a state's own rows keep their
  // place and the minors who move in follow after them.
  const stayersByGroupKey = new Map(states.map((group) => [group.key, []]));
  const arrivalsByGroupKey = new Map(states.map((group) => [group.key, []]));
  states.forEach((group) =>
    group.rows.forEach((row) => {
      const key = landingKey(row.id);
      if (key === group.key || !arrivalsByGroupKey.has(key)) {
        stayersByGroupKey.get(group.key).push(row);
      } else {
        arrivalsByGroupKey.get(key).push({ ...row, state: stateOf(states, key) });
      }
    })
  );

  return states
    .map((group) => {
      const rows = [
        ...stayersByGroupKey.get(group.key),
        ...arrivalsByGroupKey.get(group.key),
      ];
      return { ...group, count: rows.length, rows };
    })
    .filter((group) => group.rows.length > 0);
}

/** marital_status 1 is Single/Divorced: a spouse still on that record is a former one. */
const SINGLE_OR_DIVORCED = 1;

/**
 * The living spouse a tree node is drawn beside — an in-law, so first and
 * last name with the suffix or special initial, "Mayumi Tanaka",
 * "Linda Hawkins M", "Paul Smith, Jr." — or "" where the node names none, the
 * spouse is deceased (the report lists the living only), or the member is
 * recorded single/divorced.
 */
function readLivingSpouseName(node) {
  const [first, next] = toText(node?.spouse_first_name).split(/\s+/);
  if (!first) return "";
  if (Number(node?.spouse_present_or_deceased) === 2) return "";
  if (Number(node?.marital_status) === SINGLE_OR_DIVORCED) return "";

  const firstName = /^[A-Za-z]\.?$/.test(next || "") ? `${first} ${next}` : first;
  return formatDashboardFullPersonName(
    firstName,
    toText(node?.spouse_last_name),
    toText(node?.spouse_suffix),
    toText(node?.spouse_special_name)
  );
}

/**
 * The states with each descendant's living spouse named on their row —
 * `spouseName`, printed "Sole & Mayumi Tanaka" — off the family tree, since the
 * endpoint lists descendants only. A spouse lives with the descendant, so
 * they sit on the same row under the same state and count toward it.
 *
 * @param {Array} states — the groups fetchFamilyByState returns
 * @param {Map} treeByMember — indexFamilyTreeByMember over the family tree
 */
export function attachDescByStateSpouses(states, treeByMember) {
  if (!treeByMember || treeByMember.size === 0) return states;

  return states.map((group) => {
    const rows = group.rows.map((row) => {
      const spouseName = readLivingSpouseName(treeByMember.get(row.id)?.node);
      return spouseName ? { ...row, spouseName } : row;
    });
    return { ...group, rows, count: countDescByStateMembers(rows) };
  });
}

/** The people on these rows: each descendant, plus their spouse where named. */
export function countDescByStateMembers(rows) {
  return rows.reduce((sum, row) => sum + (row.spouseName ? 2 : 1), 0);
}

/**
 * The two totals the page heads with, off the states as listed: how many
 * states, and the members filed under them — spouses included — added up.
 */
export function getDescByStateTotals(states) {
  return {
    totalStates: states.length,
    totalMembers: states.reduce(
      (sum, group) => sum + countDescByStateMembers(group.rows),
      0
    ),
  };
}

/**
 * The two totals the dashboard card prints, off the dashboard statistics
 * block like most cards: states of birth and the living members born in
 * them — the same pair the report endpoint states and the page heads with.
 */
export function getDescByStateCounts(statistics) {
  return {
    states: statistics?.family_by_state_count ?? statistics?.total_states ?? 0,
    members: statistics?.total_living_members_by_state ?? 0,
  };
}

/**
 * The page as a flat run of blocks, for the two-column split: each state's
 * heading, then one block per row under it. The split is measured block by
 * block, so a state can break across the columns row by row.
 */
export function toDescByStateBlocks(states) {
  const blocks = [];

  states.forEach((group) => {
    blocks.push({ kind: "heading", key: `heading-${group.key}`, group });
    group.rows.forEach((row) => {
      blocks.push({ kind: "row", key: `row-${group.key}-${row.id}`, group, row });
    });
  });

  return blocks;
}

/**
 * The blocks cut into two columns at `splitAt` — the first `splitAt` blocks,
 * then the rest — the way the page reads them: the cut never leaves a
 * state's heading as the last thing in the first column, and where it falls
 * inside a state the second column opens with that state's heading again,
 * marked `continued`, so the rows there still read under their state. A
 * cut at or past either end comes back as one column, the whole page.
 */
export function splitDescByStateBlocks(blocks, splitAt) {
  let cut = splitAt;
  if (blocks[cut - 1]?.kind === "heading") cut -= 1;
  if (cut <= 0 || cut >= blocks.length) return [blocks];

  const first = blocks.slice(0, cut);
  const second = blocks.slice(cut);
  if (second[0].kind === "row") {
    const { group } = second[0];
    second.unshift({
      kind: "heading",
      key: `heading-${group.key}-continued`,
      group,
      continued: true,
    });
  }

  return [first, second];
}
